import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Map as MapIcon,
  MessageCircle,
  Package,
  Wallet,
  User as UserIcon,
  Bell,
  Truck,
  ShoppingBag,
  LogOut,
  Trophy,
  ClipboardList,
  TrendingUp,
  Flame,
  Users,
  MessagesSquare,
  Headphones,
  X,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import SOSButton from "@/components/SOSButton";
import BackgroundCheckBanner from "@/components/BackgroundCheckBanner";
import { ChatBadge } from "@/components/ChatBadge";
import { FALLBACK_DRIVER_MENU } from "@/lib/driverRegistration";
import { API } from "../App";

/**
 * DriverShell — shell dark do módulo Entregador (Design System GoFood).
 * Fundo #0B0D0F/preto · primária #FF6B00 · Inter · Lucide.
 *
 * Drawer lateral server-driven (mesma fonte do Drawer do GoFood-Mobile):
 * itens vêm de /api/v1/mobile/app-config (persona driver), com fallback local.
 */
const NAV = [
  { label: "Início", to: "/entregador/entregas", icon: Home, testId: "nav-home" },
  { label: "Mapa", to: "/entregador/mapa", icon: MapIcon, testId: "nav-map" },
  { label: "Chat", to: "/entregador/chat", icon: MessageCircle, testId: "nav-chat", withBadge: true },
  { label: "Pedidos", to: "/entregador/pedidos", icon: Package, testId: "nav-orders" },
  { label: "Ganhos", to: "/entregador/carteira", icon: Wallet, testId: "nav-wallet" },
  { label: "Perfil", to: "/entregador/perfil", icon: UserIcon, testId: "nav-profile" },
];

const MENU_ICONS = {
  "clipboard-list": ClipboardList,
  "trending-up": TrendingUp,
  wallet: Wallet,
  map: MapIcon,
  "message-circle": MessageCircle,
  flame: Flame,
  users: Users,
  "shopping-bag": ShoppingBag,
  "messages-square": MessagesSquare,
  headphones: Headphones,
};

export const DriverShell = ({ user, logout, children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadChats, setUnreadChats] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuItems, setMenuItems] = useState(FALLBACK_DRIVER_MENU);
  const [isOnline, setIsOnline] = useState(null);
  const [savingToggle, setSavingToggle] = useState(false);
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  // Menu server-driven — mesma configuração consumida pelo GoFood-Mobile
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!token) return;
        const r = await fetch(`${API}/v1/mobile/app-config`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const d = await r.json();
        const menu = Array.isArray(d?.menu) ? d.menu.filter((m) => m?.enabled) : null;
        if (!cancelled && menu && menu.length) {
          setMenuItems([...menu].sort((a, b) => (a.order || 0) - (b.order || 0)));
        }
      } catch {
        /* mantém fallback local */
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Status online — mesma fonte do dashboard (backend decide/valida)
  useEffect(() => {
    let cancelled = false;
    const fetchOnline = async () => {
      try {
        if (!token) return;
        const r = await fetch(`${API}/drivers/me/work-cities`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setIsOnline(!!d.is_online);
      } catch {
        /* silent */
      }
    };
    fetchOnline();
    const iv = setInterval(fetchOnline, 30000);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Badge de chat não lido (contador global — /chat/unread-total)
  useEffect(() => {
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        if (!token) return;
        const r = await fetch(`${API}/chat/unread-total`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setUnreadChats(Number(d?.total || 0));
      } catch {
        /* silent */
      }
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 30000);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleOnline = async () => {
    if (savingToggle || isOnline === null) return;
    setSavingToggle(true);
    const next = !isOnline;
    try {
      const r = await fetch(`${API}/drivers/me/online`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ is_online: next }),
      });
      if (!r.ok) {
        let code = null, msg = null;
        try { const d = await r.json(); code = d.detail?.code; msg = d.detail?.message || d.detail; } catch { /* noop */ }
        if (r.status === 401 || code === "stale_session") {
          toast.error("Sua sessão está desatualizada. Saia da conta e entre novamente.");
          return;
        }
        if (code === "face_enrollment_required" || code === "face_check_required") {
          toast.info("Verificação facial necessária — abrindo o painel");
          setDrawerOpen(false);
          navigate("/entregador/entregas");
          return;
        }
        if (code === "face_blocked") { toast.error(msg || "Verificação facial em análise pela equipe"); return; }
        throw new Error(typeof msg === "string" ? msg : "Erro ao atualizar status");
      }
      setIsOnline(next);
      toast.success(next ? "Você está ONLINE — recebendo pedidos" : "Você está OFFLINE");
    } catch (e) {
      toast.error(e.message || "Erro ao atualizar status");
    } finally {
      setSavingToggle(false);
    }
  };

  const firstName = (user?.name || "").split(" ")[0] || "Entregador";

  return (
    <div className="min-h-screen bg-black text-zinc-100" data-testid="driver-shell">
      {/* ===== HEADER ===== */}
      <header
        className="sticky top-0 z-40 bg-black/95 backdrop-blur-xl border-b border-zinc-900"
        data-testid="driver-shell-header"
      >
        <div className="px-4 pt-4 pb-3 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1
              className="text-2xl font-extrabold text-white leading-tight truncate"
              data-testid="driver-greeting"
            >
              Olá, {firstName}{" "}
              <span className="inline-block" aria-hidden>
                👋
              </span>
            </h1>
            <p className="text-xs text-zinc-500 flex items-center gap-1.5 mt-0.5">
              <Truck className="w-3.5 h-3.5" />
              Entregador GoFood
            </p>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to="/entregador/notificacoes"
              className="w-10 h-10 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-300 hover:bg-zinc-800 transition-colors"
              aria-label="Notificações"
              data-testid="driver-shell-notifications"
            >
              <Bell className="w-4 h-4" />
            </Link>
            <SOSButton driverId={user?.id} driverName={user?.name} variant="pill" />
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="w-10 h-10 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-300 hover:bg-zinc-800 transition-colors relative"
              aria-label="Menu do entregador"
              aria-expanded={drawerOpen}
              data-testid="driver-shell-profile"
            >
              <UserIcon className="w-4 h-4" />
              {isOnline !== null && (
                <span
                  className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ring-2 ring-black ${isOnline ? "bg-emerald-500" : "bg-zinc-600"}`}
                  aria-hidden
                />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* ===== DRAWER LATERAL (mesmo padrão do GoFood-Mobile) ===== */}
      {drawerOpen && (
        <>
          <div
            className="fixed inset-0 z-[1300] bg-black/60 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
            aria-hidden
            data-testid="driver-drawer-backdrop"
          />
          <aside
            className="fixed top-0 right-0 bottom-0 z-[1310] w-[320px] max-w-[88vw] bg-[#0B0D0F] border-l border-zinc-800 flex flex-col overflow-y-auto"
            role="menu"
            data-testid="driver-drawer"
          >
            {/* Cabeçalho do drawer */}
            <div className="p-4 border-b border-zinc-800 bg-zinc-900/40">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[11px] uppercase tracking-wider text-zinc-500 font-bold">Menu</p>
                <button
                  type="button"
                  onClick={() => setDrawerOpen(false)}
                  className="w-8 h-8 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white"
                  aria-label="Fechar menu"
                  data-testid="driver-drawer-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-3">
                {user?.profile_photo_url ? (
                  <img
                    src={user.profile_photo_url}
                    alt={user?.name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-[#FF6B00]/50"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-zinc-900 border-2 border-zinc-700 flex items-center justify-center text-zinc-400">
                    <UserIcon className="w-5 h-5" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-extrabold text-white truncate">{user?.name || "Entregador"}</p>
                  <p className="text-[11px] text-zinc-500 truncate">{user?.email || ""}</p>
                </div>
              </div>
            </div>

            {/* Itens server-driven */}
            <nav className="flex-1 py-2" data-testid="driver-drawer-items">
              {menuItems.map((item) => {
                const Icon = MENU_ICONS[item.icon] || ClipboardList;
                const active =
                  location.pathname === item.route ||
                  location.pathname.startsWith(item.route + "/");
                return (
                  <Link
                    key={item.key}
                    to={item.route}
                    onClick={() => setDrawerOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm font-bold transition-colors ${
                      active
                        ? "text-[#FF6B00] bg-[#FF6B00]/10 border-r-2 border-[#FF6B00]"
                        : "text-zinc-300 hover:text-white hover:bg-zinc-900"
                    }`}
                    data-testid={`drawer-item-${item.key}`}
                  >
                    <Icon className="w-5 h-5 shrink-0" strokeWidth={2.2} />
                    <span className="flex-1">{item.label}</span>
                    {item.key === "chat" && unreadChats > 0 && (
                      <ChatBadge count={unreadChats} testId="drawer-chat-badge" />
                    )}
                  </Link>
                );
              })}

              {/* Extras existentes preservados (equivalentes funcionais) */}
              <div className="mt-2 pt-2 border-t border-zinc-900">
                <Link
                  to="/bolao"
                  onClick={() => setDrawerOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-zinc-300 hover:text-white hover:bg-zinc-900"
                  data-testid="drawer-item-bolao"
                >
                  <Trophy className="w-5 h-5 text-yellow-400" strokeWidth={2.2} />
                  Bolão da Copa
                </Link>
                <Link
                  to="/cliente/home"
                  onClick={() => setDrawerOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-zinc-300 hover:text-white hover:bg-zinc-900"
                  data-testid="drawer-item-cliente"
                >
                  <ShoppingBag className="w-5 h-5 text-emerald-400" strokeWidth={2.2} />
                  Pedir como cliente
                </Link>
              </div>
            </nav>

            {/* Status Online/Offline */}
            <div className="p-4 border-t border-zinc-800">
              <button
                type="button"
                onClick={toggleOnline}
                disabled={savingToggle || isOnline === null}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-extrabold transition-colors disabled:opacity-60 ${
                  isOnline
                    ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/25"
                    : "bg-zinc-900 text-zinc-300 border border-zinc-700 hover:bg-zinc-800"
                }`}
                data-testid="drawer-online-toggle"
              >
                {savingToggle ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <span className={`w-2.5 h-2.5 rounded-full ${isOnline ? "bg-emerald-400 animate-pulse" : "bg-zinc-500"}`} />
                )}
                {isOnline === null ? "Carregando…" : isOnline ? "ONLINE — ficar offline" : "OFFLINE — ficar online"}
              </button>

              {/* Sair da conta */}
              <button
                type="button"
                onClick={() => {
                  setDrawerOpen(false);
                  if (logout) logout();
                  else navigate("/login");
                }}
                className="w-full flex items-center justify-center gap-2 mt-3 py-3 rounded-full text-sm font-bold text-red-400 bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 transition-colors"
                data-testid="drawer-logout"
              >
                <LogOut className="w-4 h-4" />
                Sair da conta
              </button>
            </div>
          </aside>
        </>
      )}

      {/* ===== PAGE CONTENT ===== */}
      <BackgroundCheckBanner user={user} />
      <main className="pb-24">{children}</main>

      {/* ===== BOTTOM NAVIGATION ===== */}
      <nav
        className="fixed bottom-0 inset-x-0 z-40 bg-black/95 backdrop-blur-xl border-t border-zinc-900 pb-[env(safe-area-inset-bottom)]"
        data-testid="driver-shell-bottomnav"
      >
        <div className="flex items-center justify-around max-w-3xl mx-auto px-2 py-2">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active =
              location.pathname === item.to ||
              location.pathname.startsWith(item.to + "/");
            return (
              <Link
                key={item.to}
                to={item.to}
                className="relative flex flex-col items-center gap-0.5 px-2 py-2 min-w-[56px]"
                data-testid={`bottom-${item.testId}`}
              >
                <div className="relative">
                  <Icon
                    className={`w-7 h-7 ${active ? "text-[#FF6B00]" : "text-zinc-500"}`}
                    strokeWidth={active ? 2.4 : 1.9}
                  />
                  {item.withBadge && unreadChats > 0 && (
                    <span
                      className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-[#25D366] text-white text-[10px] font-extrabold flex items-center justify-center leading-none ring-2 ring-black"
                      data-testid="chat-badge"
                    >
                      {unreadChats > 9 ? "9+" : unreadChats}
                    </span>
                  )}
                </div>
                <span
                  className={`text-[11px] font-semibold ${
                    active ? "text-[#FF6B00]" : "text-zinc-500"
                  }`}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default DriverShell;
